<template>
  <v-col
    v-if="enabled"
    :cols="cols"
    :lg="lg"
    :md="md"
  >
    <v-divider
      v-if="dividedTop"
      class="mt-2 mb-4"
    />

    <div :class="wrapperClasses">
      <div class="me-3">
        <div
          v-if="title"
          :class="[titleClasses, bold ? 'fs-18 fw-600' : 'fs-18 fw-500']"
          class="c-black d-inline"
        >
          {{ $bct(title) }}
          <HelpIcon
            :document="helpDocument"
            :on-help-click="openHelpFlyout"
          />
        </div>

        <div
          v-if="subtitle"
          class="fs-16 c-light-black mt-1 text-pre-wrap"
        >
          {{ $bct(subtitle) }}
        </div>
      </div>

      <div class="d-flex align-center">
        <v-tooltip location="bottom">
          <template #activator="{ props: activatorProps }">
            <span v-bind="activatorProps">
              <v-icon
                v-if="locked"
                class="me-1"
                color="grey"
                data-testid="lock-icon"
                icon="lock"
              />
            </span>
          </template>
          <span v-if="hardLock">{{ $bct(hardLockMessage) }}</span>
        </v-tooltip>

        <v-switch
          @update:model-value="handleSwitchClick"
          :aria-label="$bct(title)"
          :data-testid="switchTestId || undefined"
          :density="dense || veryDense ? 'compact' : undefined"
          :disabled="locked"
          :loading="processing"
          :model-value="localValue"
          :readonly="readonly"
          class="mt-0 pt-0"
          color="primary"
          role="switch"
          variant="filled"
          data-cy
          hide-details
          inset
          tile
        />
      </div>
    </div>

    <slot />

    <v-divider
      v-if="dividedBottom"
      class="mt-4 mb-2"
    />
  </v-col>
</template>

<script setup>
import HelpIcon from '@/shared/components/form/HelpIcon.vue';
import propsToRefs from '@/shared/utils/propsToRefs';
import useLabeledField from '@/shared/composables/useLabeledField';
import useSchematizedField from '@/shared/composables/useSchematizedField';

const $bct = inject('translator');

const props = defineProps({
  dividedBottom: {
    type: Boolean,
    default: false,
  },
  dividedTop: {
    type: Boolean,
    default: false,
  },
  processing: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  subtitle: {
    type: String,
    default: '',
  },
  switchTestId: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: '',
  },
  titleClasses: {
    type: [String, Array, Object],
    default: undefined,
  },
  wrapperClasses: {
    type: [String, Array, Object],
    default: 'd-flex align-center justify-space-between',
  },
  ...useLabeledField.props,
  ...useSchematizedField.props,
});

defineEmits(useLabeledField.emits);

const localValue = defineModel({ type: [Boolean, String, Number] });

const { enabled, helpDocument, locked, openHelpFlyout } = useSchematizedField(
  ...propsToRefs(props, useSchematizedField.paramKeys),
);

const { handleBlur, handleUpdateModelValue } = useLabeledField(
  ...propsToRefs(props, useLabeledField.paramKeys),
);

const handleSwitchClick = function (event) {
  // Fire off blur event so the provider show can still update when you expect it to.
  // passing the blur event through to the v-switch component directly causes a race
  // condition where the model value hasn't changed for the blur event yet
  handleUpdateModelValue(event);
  handleBlur(event);
};
</script>

<style scoped>
.v-input--horizontal {
  grid-template-areas: unset;
}
</style>
